import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * UserSearchDropdown component for searching and selecting users
 */
const UserSearchDropdown = ({
                                onUserSelect,
                                name,
                                placeholder = "Type to search...",
                                className = "",
                                clearSelection = false
                            }) => {
    // State for search results, loading state, and selected user
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    // References
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Debounce timer reference
    const debounceTimerRef = useRef(null);

    // Handle input change with debounce
    const handleInputChange = (e) => {
        const searchQuery = e.target.value.trim();

        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // If the input is empty, clear results and don't search
        if (!searchQuery) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        // Set loading state
        setIsLoading(true);
        setShowDropdown(true);

        // Debounce the search to avoid too many requests
        debounceTimerRef.current = setTimeout(() => {
            fetchUsers(searchQuery);
        }, 300);
    };

    // Fetch users from API
    const fetchUsers = async (query) => {
        try {
            if (query != null && query.length < 2) {
                return;
            }
            // Replace with your actual API endpoint
            const response = await fetch(`/otgservices/findUser.php?search=${encodeURIComponent(query)}`);
            const data = await response.json();

            // Update search results
            setSearchResults(data);
            setIsLoading(false);
        } catch (error) {
            console.error('Error searching users:', error);
            setSearchResults([]);
            setIsLoading(false);
        }
    };

    // Handle user selection
    const handleSelectUser = (user) => {
        setShowDropdown(false);

        // Clear the input field after selection
        if (inputRef.current) {
            inputRef.current.value = '';
        }

        // Pass the selected user to the parent component
        if (onUserSelect) {
            onUserSelect(user);
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle clearing selection when clearSelection prop changes
    useEffect(() => {
        if (clearSelection) {
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }
    }, [clearSelection]);

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                name={name}
                placeholder={placeholder}
                onChange={handleInputChange}
                onFocus={() => inputRef.current.value && setShowDropdown(true)}
                className={className}
                autoComplete="off"
            />

            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
                >
                    {isLoading ? (
                        <div className="p-2 text-center text-gray-500">Loading...</div>
                    ) : searchResults.length > 0 ? (
                        <ul>
                            {searchResults.map((user) => (
                                <li
                                    key={user.id}
                                    className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    onClick={() => handleSelectUser(user)}
                                >
                                    <div className="flex items-center">
                                        {user.avatar && (
                                            <img
                                                src={user.avatar}
                                                alt={`${user.name}'s avatar`}
                                                className="w-8 h-8 rounded-full mr-2"
                                            />
                                        )}
                                        <div>
                                            <div className="font-medium">{user.name}</div>
                                            {user.username && (
                                                <div className="text-xs text-gray-500">@{user.username}</div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="p-2 text-center text-gray-500">No users found</div>
                    )}
                </div>
            )}
        </div>
    );
};

UserSearchDropdown.propTypes = {
    onUserSelect: PropTypes.func.isRequired,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    clearSelection: PropTypes.bool
};

export default UserSearchDropdown;